import type { BackgroundPatternName } from "./Background.types";

export type LatticePoint = { x: number; y: number };

export type Lattice = {
  points: LatticePoint[];
  // Point indices per shape; empty for dots, where every point is its own dot
  shapes: number[][];
  // Which shapes each point belongs to, so a moved point only redraws those
  shapesByPoint: number[][];
};

const HEX_ROW_RATIO = Math.sqrt(3);

const EMPTY_LATTICE: Lattice = { points: [], shapes: [], shapesByPoint: [] };

// Packs a rounded (x, y) into one number; wide enough for any viewport.
const KEY_STRIDE = 1_000_000;

// A lattice is rebuilt whenever the box changes, so it always covers it with
// one row and one column to spare on each side.
export function buildLattice(
  pattern: BackgroundPatternName,
  cellSize: number,
  width: number,
  height: number,
): Lattice {
  if (width <= 0 || height <= 0 || cellSize <= 0) {
    return EMPTY_LATTICE;
  }

  if (pattern === "dots") {
    return buildDots(cellSize, width, height);
  }

  if (pattern === "hexagons") {
    return buildHexagons(cellSize, width, height);
  }

  return buildGrid(cellSize, width, height);
}

function buildDots(cell: number, width: number, height: number): Lattice {
  const points: LatticePoint[] = [];

  for (let y = cell / 2; y < height + cell; y += cell) {
    for (let x = cell / 2; x < width + cell; x += cell) {
      points.push({ x, y });
    }
  }

  return { points, shapes: [], shapesByPoint: [] };
}

function buildGrid(cell: number, width: number, height: number): Lattice {
  const columns = Math.ceil(width / cell) + 1;
  const rows = Math.ceil(height / cell) + 1;

  const points: LatticePoint[] = [];

  for (let row = 0; row <= rows; row += 1) {
    for (let column = 0; column <= columns; column += 1) {
      points.push({ x: column * cell, y: row * cell });
    }
  }

  const index = (row: number, column: number) => row * (columns + 1) + column;
  const shapes: number[][] = [];

  // One closed cell per shape: shared edges are drawn twice, which is
  // invisible once the layer is flattened, and it keeps a moved corner's
  // redraw down to the cells that own it.
  for (let row = 0; row < rows; row += 1) {
    for (let column = 0; column < columns; column += 1) {
      shapes.push([
        index(row, column),
        index(row, column + 1),
        index(row + 1, column + 1),
        index(row + 1, column),
      ]);
    }
  }

  return { points, shapes, shapesByPoint: mapShapesByPoint(points, shapes) };
}

function buildHexagons(side: number, width: number, height: number): Lattice {
  const hexWidth = HEX_ROW_RATIO * side;
  const rowStep = 1.5 * side;

  const points: LatticePoint[] = [];
  const shapes: number[][] = [];

  // Vertices are shared between neighbours, so the honeycomb stays welded
  // when they move. Keyed by number rather than by string: this runs a few
  // thousand times per rebuild.
  const byKey = new Map<number, number>();

  const pointAt = (x: number, y: number): number => {
    const key = Math.round(x * 100) * KEY_STRIDE + Math.round(y * 100);
    const existing = byKey.get(key);

    if (existing !== undefined) {
      return existing;
    }

    points.push({ x, y });
    byKey.set(key, points.length - 1);

    return points.length - 1;
  };

  const rows = Math.ceil(height / rowStep) + 2;
  const columns = Math.ceil(width / hexWidth) + 2;

  for (let row = -1; row < rows; row += 1) {
    const offset = (row & 1) === 0 ? 0 : hexWidth / 2;

    for (let column = -1; column < columns; column += 1) {
      const cx = column * hexWidth + offset;
      const cy = row * rowStep;
      const half = side / 2;
      const halfWidth = hexWidth / 2;

      shapes.push([
        pointAt(cx, cy - side),
        pointAt(cx - halfWidth, cy - half),
        pointAt(cx - halfWidth, cy + half),
        pointAt(cx, cy + side),
        pointAt(cx + halfWidth, cy + half),
        pointAt(cx + halfWidth, cy - half),
      ]);
    }
  }

  return { points, shapes, shapesByPoint: mapShapesByPoint(points, shapes) };
}

function mapShapesByPoint(
  points: LatticePoint[],
  shapes: number[][],
): number[][] {
  const map: number[][] = points.map(() => []);

  shapes.forEach((shape, shapeIndex) => {
    shape.forEach((pointIndex) => {
      map[pointIndex]?.push(shapeIndex);
    });
  });

  return map;
}
